import React from 'react';
import { always, equals, ifElse, includes, isNil, pathOr, prop, take } from 'ramda';
import { Group } from '@vx/group';
import { scaleBand, scaleLinear, scaleOrdinal, scaleTime } from '@vx/scale';
import { ParentSize } from '@vx/responsive';
import { LinePath } from '@vx/shape';
import { useTooltipInPortal } from '@vx/tooltip';

import { useIntl } from 'gatsby-plugin-intl';
import { renderWhenTrue } from '../../../utils/rendering';
import { findMaxByKeys } from '../../../utils/findMaxByKeys';
import { NoData, isNoData } from '../noData';
import { Header } from '../header';
import { Bar } from '../bar';
import { Axes } from '../axes';
import { Grid } from '../grid';
import { parseData } from '../charts.helper';
import { Container, Legend, Chart, Svg } from '../charts.styles';
import { CHART_TYPES, DEFAULT_SIZE } from '../charts.constants';
import { CHART_PALETTE } from '../../../utils/colors';
import { BAR_DEFAULT } from '../configs/bar.config';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal/tooltipWithPortal.component';
import { toFixed } from '../../../utils/math';
import { TooltipElementTable, TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';
import { useGatsbyContext } from '../../../store/GatsbyContext';
import { findPiLabel } from '../../../utils/findPiLabel';
import { curveCardinal, curveCatmullRomOpen, curveNatural } from '@vx/curve';
import { useWindowLocation } from '../../../hooks/useWindowLocation';

const standarizeDashes = (item) => item && item.replace(/[^a-z0-9]/gi, '');
const standarize = (array) => array.map(standarizeDashes);

export const renderBar = (bar, groupIndex, barIndex) => {
  return <Bar key={`bar-group-bar-${groupIndex}-${barIndex}`} {...bar} />;
};

const renderLegend = ({ config, ...props }) => {
  return renderWhenTrue(() => <Legend config={config} {...props} />)(config.legendVisibility);
}


const getScales = ({ chartWidth, chartHeight, type, data, series, axis, config: { groupPadding, padding } }) => {
  const getAxis = prop(axis);

  const groupScale = scaleTime({
    domain: [data[0].date, data[data.length - 1].date],
    nice: true,
  })

  const innerScale = scaleBand({
    domain: series,
    padding,
  });

  const maxByKeys = findMaxByKeys(data, series);


  const valueScale = scaleLinear({
    domain: [0, maxByKeys > 4 ? maxByKeys : 4],
    nice: true,
  });

  const { barScalesProps, gridScalesProps } = ifElse(
    equals(CHART_TYPES.HORIZONTAL),
    () => {
      groupScale.rangeRound([0, chartHeight]);
      innerScale.rangeRound([0, groupScale.bandwidth()]);
      valueScale.rangeRound([0, chartHeight]);

      return {
        barScalesProps: {
          // yScale: yScale,
          // xScale: xScale,
        },
        gridScalesProps: {
          xScale: xScale,
        },
      };
    },
    () => {
      groupScale.rangeRound([0, chartWidth]);
      valueScale.rangeRound([chartHeight, 0]);

      return {
        barScalesProps: {
          xScale: groupScale,
          yScale: valueScale,
        },
        gridScalesProps: {
          yScale: valueScale,
        },
      };
    }
  )(type);

  const axesScalesProps = {
    groupScale,
    valueScale,
  };

  return { axesScalesProps, barScalesProps, gridScalesProps };
};


export const LineChartCore = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  chartData = {},
  renderBarGroup,
  type,
  groupingComponent: BarGroupComponent,
  config: customConfig = {},
  customParser,
  tabs,
  showTooltip = true,
} = {}) => {
  const { formatMessage } = useIntl();
  if (customParser) customParser(chartData);

  const tooltip = useChartTooltipContext();
  const { containerRef } = useTooltipInPortal();
  const getWindowLocation = useWindowLocation();
  const windowLocation = getWindowLocation() || {};
  const hash = windowLocation.hash
  let stroke = '#3A5aC5'

  if (hash === '#colombia') stroke = '#36A489'
  if (hash === '#mongolia') stroke = '#C05EF3'
  if (hash === '#seychelles') stroke = '#3A5AC5'

  const { title, subtitle, axis, longName, data: rawData, labels = {}, series } = chartData;

  // scales

  const xKey = axis

  const yKey = series[0]

  const getX = (d) => d[xKey];
  const getY = (d) => d[yKey];


  const filteredData = rawData.filter(dataObj => {
    if (dataObj[xKey] === null || dataObj[yKey] === null) return false
    return true
  })

  if (isNoData(axis, rawData, series)) return <NoData width={width} height={height} />;

  let data = parseData(rawData, series);
  data = data.map((item) => {
    if (item?.agency) {
      item.agency = formatMessage({ id: item.agency });
      return item;
    }
    if (item?.region) {
      item.region = formatMessage({ id: item.region });
      return item;
    }
    if (item?.pillar) {
      item.pillar = formatMessage({ id: item.pillar });
      return item;
    }
    if (item?.indicator) {
      item.indicator = formatMessage({ id: item.indicator });
      return item;
    }
    return item;
  });
  const config = {
    ...BAR_DEFAULT,
    ...customConfig,
  };

  let color = scaleOrdinal({
    domain: series,
    range: take(series.length, config.colors),
  });

  if (hash === '#colombia') color = scaleOrdinal({
    domain: series,
    range: take(series.length, ['#36A489']),
  });

  if (hash === '#mongolia') color = scaleOrdinal({
    domain: series,
    range: take(series.length, ['#C05EF3']),
  });

  if (hash === '#seychelles') color = scaleOrdinal({
    domain: series,
    range: take(series.length, ['#3A5AC5']),
  });



  const yOffset = config.yAxisVisible ? config.yAxisSpace : 0;
  const xOffset = config.xAxisVisible ? config.xAxisSpace : 0;

  const renderTooltipContent = () => {
    const VALUE_COLUMN_NAME = 'Score';
    const { piLabels } = useGatsbyContext();
    const currentAxis = tooltip.data && (data[tooltip.data.index][longName] || data[tooltip.data.index][axis]);
    const piLabelsIndicators = piLabels.map(prop('indicator'));
    const { formatMessage } = useIntl();

    const currPiLabel = includes(standarizeDashes(currentAxis), standarize(piLabelsIndicators)) ? currentAxis : '';
    const getValue = (item) => (currPiLabel ? `${currPiLabel}: ${labels[item.key]}` : labels[item.key]);

    const itemsByIndicator = Object.values(labels).filter((label) =>
      standarize(piLabelsIndicators).includes(standarizeDashes(label))
    );

    const getValueByIndicator = (item, index) =>
      itemsByIndicator.length > 0
        ? `${itemsByIndicator[index]}: ${findPiLabel(piLabels, getValue(item))}`
        : getValue(item);

    return (
      tooltip.data && (
        <TooltipElementTable>
          <thead>
            <tr>
              <th>{formatMessage({ id: currentAxis })}</th>
              <th>{formatMessage({ id: VALUE_COLUMN_NAME })}</th>
            </tr>
          </thead>
          <tbody>
            {tooltip.data.bars.map(
              (item, index) =>
                item.value && (
                  <tr key={`${item.value}-${item.key}`}>
                    <TooltipTableDataCell>
                      {formatMessage({ id: getValueByIndicator(item, index) })}
                    </TooltipTableDataCell>
                    <TooltipTableDataCell color={item.color}>
                      <span>{toFixed(item.value, 1)}</span>
                    </TooltipTableDataCell>
                  </tr>
                )
            )}
          </tbody>
        </TooltipElementTable>
      )
    );
  };

  const hoveredGroupIndex = pathOr(null, ['data', 'index'], tooltip);
  const isHoverActive = !isNil(hoveredGroupIndex);

  return (
    <Container width={width}>
      <Header title={title} subtitle={subtitle} xOffset={xOffset} tabs={tabs} config={config} >
        {renderLegend({ labels, colorScale: color, config })}
      </Header>
      <Chart width={width} height={height} ref={containerRef}>
        {showTooltip &&
          renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}
        <ParentSize>
          {({ width: chartAreaWidth, height: chartAreaHeight }) => {
            if (!chartAreaWidth || !chartAreaHeight) return null;

            const chartWidth = chartAreaWidth - xOffset;
            const chartHeight = chartAreaHeight - yOffset;
            const { axesScalesProps, barScalesProps, gridScalesProps } = getScales({
              chartWidth,
              chartHeight,
              type,
              data,
              series,
              axis,
              config,
            });


            return (
              <Svg>
                <Grid left={xOffset} width={chartWidth} height={chartHeight} config={config} {...gridScalesProps}>
                  <LinePath
                    curve={curveCardinal}
                    data={filteredData}
                    x={(d, i) => barScalesProps.xScale(getX(d))}
                    y={(d, i) => barScalesProps.yScale(getY(d))}
                    stroke={stroke}
                    strokeWidth={2.5}
                  />
                </Grid>
                <Axes
                  type={type}
                  chartHeight={chartHeight}
                  chartWidth={chartWidth}
                  config={config}
                  {...axesScalesProps}
                />
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
